import { useState, type ReactNode } from 'react';
import { Button, Form, InlineStack, Stack, Text, TextField } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { useZodForm } from '@/components/form';
import { useCreateSegment, type SegmentConditions } from '@/services/db/segment/mutations';
import { sentryCaptureException } from '@/utils/sentry';
import { isRecord } from '@/utils/objects';
import type { JsonValue } from '@/features/dryad';

const schema = z.object({
  name: z.string().trim().min(1, 'Enter a name for the segment.'),
});

/**
 * The builder serializes to a boolean group at the root, which is what the
 * endpoint stores. Anything else means the tree never reached a saveable
 * shape, and the submit gate above should already have caught it.
 */
function asSegmentConditions(serialized: JsonValue): SegmentConditions {
  if (isRecord(serialized)) {
    if (Array.isArray(serialized.and)) return { and: serialized.and };
    if (Array.isArray(serialized.or)) return { or: serialized.or };
    if ('not' in serialized) return { not: serialized.not };
  }
  throw new Error('The segment conditions did not serialize to a group');
}

export type CreateSegmentFormProps = {
  /** Receives the new segment's id once the API has created it. */
  onCreated: (segmentId: number) => void;
  onCancel: () => void;
  /**
   * Renders the new segment's own condition builder. Supplied by the host so
   * this module doesn't import the builder it is rendered inside.
   */
  renderConditions: (onChange: (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => void) => ReactNode;
};

const formStyle = {
  padding: themeVars.space['200'],
  border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
  borderRadius: themeVars.radius['4'],
};

/**
 * The inline create-segment form the segment picker's "create a new segment"
 * entry opens. Collects a name and the new segment's conditions, then reports
 * the created id to the host, which lands it as a condition row.
 */
export function CreateSegmentForm({ onCreated, onCancel, renderConditions }: CreateSegmentFormProps) {
  const form = useZodForm({ schema, mode: 'onSubmit', defaultValues: { name: '' } });
  const createSegment = useCreateSegment();

  // The nested builder reports its serialized tree and validity on every edit.
  const [conditions, setConditions] = useState<JsonValue>(null);
  const [conditionsAreValid, setConditionsAreValid] = useState(true);
  const [conditionsAreEmpty, setConditionsAreEmpty] = useState(true);

  // Shown only after a submit attempt, so the form doesn't open in red.
  const [conditionsError, setConditionsError] = useState<string | null>(null);

  const handleConditionsChange = (serialized: JsonValue, isValid: boolean, isEmpty: boolean) => {
    setConditions(serialized);
    setConditionsAreValid(isValid);
    setConditionsAreEmpty(isEmpty);
    if (isValid && !isEmpty) {
      setConditionsError(null);
    }
  };

  // The conditions gate, in the same order and wording as the Ember form's.
  const conditionsProblem = (): string | null => {
    if (conditionsAreEmpty) {
      return 'Add some conditions for this segment.';
    }
    if (!conditionsAreValid) {
      return 'Fix the errors marked above.';
    }
    return null;
  };

  const onSubmit = form.handleSubmit(async ({ name }) => {
    // The conditions aren't a Zod field (they come from the builder), so their
    // gate lives here with the rest of the submit guard.
    const problem = conditionsProblem();
    if (problem != null) {
      setConditionsError(problem);
      return;
    }
    setConditionsError(null);
    try {
      // No `type`: the API defaults a new segment to dynamic, which is what
      // this shortcut creates — and a dynamic segment requires its conditions.
      const created = await createSegment.mutateAsync({
        name: name.trim(),
        conditions: asSegmentConditions(conditions),
      });
      onCreated(Number(created.id));
    } catch (error) {
      sentryCaptureException(error instanceof Error ? error : new Error(String(error)));
      form.setError('name', { message: 'Unable to create the segment. Please try again.' });
    }
  });

  return (
    // Pluma's Form locks its fields while onSubmit's promise is pending
    // (autoLoading), so a double submit can't reach the mutation.
    <Form data-testid="create-segment-form" onSubmit={onSubmit} style={formStyle} mt="200">
      <Stack gap="200">
        {renderConditions(handleConditionsChange)}

        {conditionsError != null ? <Text color="critical">{conditionsError}</Text> : null}

        <InlineStack gap="100" alignBlock="end" wrap>
          <TextField {...form.registerPluma('name')} label="Segment name" placeholder="Segment name" />

          {/* Submit stays enabled while invalid so the click surfaces the
              errors, per the repo's form rules. */}
          <Button type="submit" variant="primary" isLoading={createSegment.isPending}>
            Create segment
          </Button>

          <Button type="button" variant="secondary" onClick={onCancel} isDisabled={createSegment.isPending}>
            Cancel
          </Button>
        </InlineStack>
      </Stack>
    </Form>
  );
}
